/**
 * 4K PA Screener — scoring, bands, and the administration decision rules.
 *
 * Pure functions only (no React, no Firestore) so the workflow rules that
 * matter clinically are unit-testable in isolation. The UI in
 * src/pages/pa/PaScreener.js is glue over this module.
 *
 * Two distinctions this module exists to protect:
 *
 *  1. NOT ADMINISTERED is not INCORRECT. A discontinued step's remaining
 *     items are unanswered, not wrong. Scoring them as wrong would report a
 *     child who was never asked as a child who failed — which is the exact
 *     signal the instrument exists to produce. `scoreStep` counts correct
 *     answers only, and `administrationOf` reports the state separately so
 *     every downstream surface can render it as its own thing.
 *
 *  2. SUGGEST is not ENFORCE. Skip-if-proficient and discontinue are both
 *     offers with a visible reason and a one-tap override. Neither ever
 *     auto-scores, auto-advances, or auto-marks anything.
 *
 * There is deliberately NO composite score. The five steps are reported
 * separately, per the district's paper form; averaging them away is exactly
 * how you lose the per-skill signal that makes the screener useful.
 */
import {
  PA_BANDS,
  PA_PROFICIENT_MIN,
  PA_ITEMS_PER_STEP,
  PA_WINDOW_BY_QUARTER,
  PA_FORM_BY_WINDOW,
  PA_WINDOWS,
  PA_WINDOW_MAY,
  PA_SCREENER_ID,
} from '../constants/paScreenerItems';

// ─── Administration states ───────────────────────────────────────────────
export const ADMIN_ADMINISTERED = 'administered';
export const ADMIN_NOT_ADMINISTERED = 'not_administered';
export const ADMIN_DISCONTINUED = 'discontinued';

/** Item response values. `null`/absent = not yet asked. */
export const RESP_CORRECT = 'Correct';
export const RESP_INCORRECT = 'Incorrect';
export const RESP_NOT_ADMINISTERED = 'NotAdministered';

/** Consecutive misses at the START of a step before we offer to stop. */
export const DISCONTINUE_AFTER_CONSECUTIVE_MISSES = 3;

/** Soft per-item pacing hint, in ms. Visual only — never auto-advances. */
export const ITEM_PACING_MS = 4000;

// ─── Scoring and bands ───────────────────────────────────────────────────

/** Count of correct responses. Unanswered and not-administered never count. */
export function scoreStep(responses = {}) {
  return Object.values(responses).filter((r) => r === RESP_CORRECT).length;
}

/**
 * Band for a step score. Returns null for a step that was not administered
 * — an un-given step has no band, and rendering one would invent a result.
 */
export function bandForScore(score, { administration = ADMIN_ADMINISTERED } = {}) {
  if (administration === ADMIN_NOT_ADMINISTERED) return null;
  if (typeof score !== 'number' || Number.isNaN(score)) return null;
  const band = PA_BANDS.find((b) => score >= b.min && score <= b.max);
  return band ? { id: band.id, label: band.label } : null;
}

export function isProficient(score) {
  return typeof score === 'number' && score >= PA_PROFICIENT_MIN;
}

/**
 * A discontinued step still has a real score (what the child got right
 * before stopping) but its unanswered items stay unanswered.
 */
export function administrationOf(responses = {}, { discontinued = false } = {}) {
  const answered = Object.values(responses).filter(
    (r) => r === RESP_CORRECT || r === RESP_INCORRECT
  ).length;
  if (discontinued) return ADMIN_DISCONTINUED;
  if (answered === 0) return ADMIN_NOT_ADMINISTERED;
  return ADMIN_ADMINISTERED;
}

/** Items the child was never asked — reported, never scored as wrong. */
export function notAdministeredCount(responses = {}, itemCount = PA_ITEMS_PER_STEP) {
  const answered = Object.values(responses).filter(
    (r) => r === RESP_CORRECT || r === RESP_INCORRECT
  ).length;
  return Math.max(0, itemCount - answered);
}

// ─── Windows and forms ───────────────────────────────────────────────────

/**
 * Suggested window for an administration date. The teacher sees this and
 * can change it in one tap before submitting — windows slip in real
 * schools and the teacher is the authority, not the calendar.
 */
/**
 * Current quarter label from the Academic Calendar — the SAME resolution
 * semantics AssessmentForm's getCurrentQuarter uses (v3.57.0 round 3):
 * match today against this year's and last year's quarter windows; if a
 * next-year Q1 has already started, use it; otherwise fall back to the
 * active school year's Q1 (so an August date between school years files
 * forward, exactly like every other assessment).
 *
 * @param quartersData  getCalendarQuarters() shape: { [year]: { 1..4: {start,end} } }
 * @param activeStartYear  resolveActiveSchoolYear().startYear
 */
export function quarterForDate(quartersData, activeStartYear, date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  const years = [String(d.getFullYear()), String(d.getFullYear() - 1)];
  for (const year of years) {
    const yearQuarters = (quartersData || {})[year] || {};
    for (const qi of Object.keys(yearQuarters)) {
      const { start, end } = yearQuarters[qi];
      if (start && end && d >= new Date(start) && d <= new Date(end)) return `${year}Q${qi}`;
    }
  }
  const nextYear = String(d.getFullYear() + 1);
  const nextQ1 = ((quartersData || {})[nextYear] || {})['1'];
  if (nextQ1?.start && d >= new Date(nextQ1.start)) return `${nextYear}Q1`;
  return `${activeStartYear}Q1`;
}

/**
 * Window derived from a quarter label via PA_WINDOW_BY_QUARTER — the
 * screener's collection point is the app's quarter machinery, never a
 * parallel month table and never a teacher-facing primary choice.
 */
export function windowForQuarter(quarterLabel) {
  const qi = Number(String(quarterLabel).split('Q')[1]);
  return PA_WINDOW_BY_QUARTER[qi] ?? 'september';
}

export function formForWindow(window) {
  return PA_FORM_BY_WINDOW[window] ?? 'A';
}

export function windowLabel(window) {
  return PA_WINDOWS.find((w) => w.id === window)?.label ?? window;
}

export function isWindowOptional(window) {
  return PA_WINDOWS.find((w) => w.id === window)?.optional === true;
}

// ─── Discontinue rule (2c) ───────────────────────────────────────────────

/**
 * True when the FIRST N responses of a step are all incorrect — i.e. the
 * child has missed from the start, which is when stopping is humane.
 * A child who misses three in the middle after answering earlier items is
 * NOT offered a discontinue: they are engaged with the task.
 *
 * @param orderedResponses item responses in administration order
 */
export function shouldOfferDiscontinue(orderedResponses = []) {
  const answered = orderedResponses.filter(
    (r) => r === RESP_CORRECT || r === RESP_INCORRECT
  );
  if (answered.length < DISCONTINUE_AFTER_CONSECUTIVE_MISSES) return false;
  return answered
    .slice(0, DISCONTINUE_AFTER_CONSECUTIVE_MISSES)
    .every((r) => r === RESP_INCORRECT);
}

// ─── Skip-if-proficient (3) ──────────────────────────────────────────────

/**
 * Suggest skipping a step the student already mastered EARLIER THIS SCHOOL
 * YEAR. Suggestion only: the UI shows the reason and offers "assess anyway".
 *
 * @param priorSteps  prior step results for this student, this school year:
 *                    [{ testType, window, score, administration }]
 * @returns {{ suggest: boolean, reason: string|null, priorWindow: string|null, priorScore: number|null }}
 */
export function skipSuggestion(priorSteps = [], testType, currentWindow) {
  const earlier = priorSteps.filter(
    (p) => p.testType === testType
      && p.window
      && p.window !== currentWindow
      && p.administration !== ADMIN_NOT_ADMINISTERED
      && isProficient(p.score)
  );
  if (earlier.length === 0) {
    return { suggest: false, reason: null, priorWindow: null, priorScore: null };
  }
  // Most recent proficient result wins the explanation.
  const best = earlier[earlier.length - 1];
  return {
    suggest: true,
    reason: `Scored ${best.score}/${PA_ITEMS_PER_STEP} (Proficient) in ${windowLabel(best.priorWindowLabel || best.window)}`,
    priorWindow: best.window,
    priorScore: best.score,
  };
}

// ─── Re-assessment flag (3) ──────────────────────────────────────────────

/**
 * A student below Proficient on a step in an earlier window is flagged for
 * re-assessment of THAT step in May.
 */
export function needsMayReassessment(priorSteps = [], testType) {
  const earlier = priorSteps.filter(
    (p) => p.testType === testType
      && p.administration !== ADMIN_NOT_ADMINISTERED
      && typeof p.score === 'number'
  );
  if (earlier.length === 0) return false;
  const latest = earlier[earlier.length - 1];
  return !isProficient(latest.score);
}

export function reassessmentTargets(priorSteps = [], stepTypes = []) {
  return stepTypes.filter((t) => needsMayReassessment(priorSteps, t));
}

/** True when this window is the one re-assessment flags point at. */
export function isReassessmentWindow(window) {
  return window === PA_WINDOW_MAY;
}

// ─── Document shape ──────────────────────────────────────────────────────

/**
 * Additive fields stamped on each step's `assessments` document. The doc
 * otherwise keeps the shape every other assessment uses — including the
 * existing `quarter` stamp, so nothing that reads quarters notices this
 * feature exists.
 *
 * `incorrectResponses` mirrors the v3.27.0 letter-knowledge field exactly:
 * a top-level map of item → what the child actually said. Same collection,
 * same rules, same auth, deletion follows the parent doc. Deliberately NOT
 * added to ASSESSMENT_EXPORT_FIELDS (see the PR body).
 */
export function buildStepDoc({
  studentId, classId, teacherId, quarter, window, formId, step, testType,
  responses = {}, incorrectResponses = {}, discontinued = false,
  itemCount = PA_ITEMS_PER_STEP, sessionId, status = 'completed', now = new Date(),
}) {
  const administration = administrationOf(responses, { discontinued });
  const score = scoreStep(responses);
  return {
    studentId,
    classId,
    teacherId,
    quarter,                 // unchanged shared calendar stamp
    testType,
    testCategory: 'phonological_awareness',
    responses,
    ...(Object.keys(incorrectResponses).length > 0 ? { incorrectResponses } : {}),
    correctCount: score,
    totalLetters: itemCount, // scale comes from the doc, never assumed
    timestamp: now,
    status,
    // --- screener grouping (additive; legacy docs simply lack these) ---
    screenerId: PA_SCREENER_ID,
    screenerStep: step,
    formId,
    window,
    band: bandForScore(score, { administration })?.id ?? null,
    administration,
    notAdministered: notAdministeredCount(responses, itemCount),
    sessionId,
  };
}

/** A doc belongs to the screener only when it carries the screener stamp. */
export function isScreenerDoc(doc) {
  return !!doc && doc.screenerId === PA_SCREENER_ID;
}

/**
 * Max score for a doc — ALWAYS read from the document, never assumed from
 * the testType. A legacy benchmark `first_sound` doc (test retired v3.67.0)
 * is 25 items; the screener's step 5 (`pa_onset_fluency`) is 5. Any code
 * that hardcodes a denominator for an onset-flavoured type will eventually
 * render a 4/5 as 4/25.
 */
export function totalPossibleOf(doc) {
  const n = Number(doc?.totalLetters);
  if (Number.isFinite(n) && n > 0) return n;
  const responses = doc?.responses;
  if (responses && typeof responses === 'object') return Object.keys(responses).length;
  return null;
}

// ─── Session / resume ────────────────────────────────────────────────────

/**
 * Where to resume an interrupted sitting: the first step that is neither
 * complete nor explicitly skipped, and within it the first unanswered item.
 * A teacher WILL be interrupted by a room full of four-year-olds; resuming
 * must land on the exact item they left.
 *
 * v3.97.1: the step the teacher was last in (`session.at`, kept by the
 * screener) wins while it is still open — a step opened out of order (picked
 * on the Dashboard) is where a reload lands, not step 1. A sitting without
 * `at`, or whose `at` step is resolved, resumes in order as before.
 */
export function resumePoint(session, stepTypes, itemCount = PA_ITEMS_PER_STEP) {
  const steps = session?.steps || {};
  const pointIn = (i) => {
    const testType = stepTypes[i];
    const s = steps[testType];
    if (!s) return { step: i + 1, testType, itemIndex: 0 };
    if (s.skipped || s.discontinued || s.submitted) return null;
    const responses = s.responses || {};
    for (let idx = 0; idx < itemCount; idx += 1) {
      if (!responses[idx] && responses[idx] !== 0) {
        return { step: i + 1, testType, itemIndex: idx };
      }
    }
    // Every item answered but the step not saved (v3.97.1): land on its last
    // item, where its Save is — never skip a step that still needs saving.
    return { step: i + 1, testType, itemIndex: itemCount - 1 };
  };
  const at = session?.at ? stepTypes.indexOf(session.at) : -1;
  if (at >= 0) {
    const point = pointIn(at);
    if (point) return point;
  }
  for (let i = 0; i < stepTypes.length; i += 1) {
    const point = pointIn(i);
    if (point) return point;
  }
  return null; // every step resolved — the sitting is complete
}

/**
 * PA Results (v3.97.1): studentId → window → testType → the LATEST doc by
 * timestamp (a step given again in the same window saves a second doc; the
 * newer one is the result). Rows with equal or missing timestamps keep their
 * order, the later one winning, as before. A "not administered" doc (a Save
 * with no answers) never hides an administered one (v3.97.2) — the Dashboard's rule
 * (dashboardAssessments latestByColumn); it shows only when nothing else is
 * there.
 */
export function latestStepDocs(rows, classId) {
  const tsOf = (a) => a?.timestamp?.toMillis?.() ?? a?.timestamp?.getTime?.() ?? 0;
  const out = {};
  (rows || [])
    .filter((r) => !classId || r.classId === classId)
    .map((r, i) => ({ r, i }))
    .sort((a, b) => (tsOf(a.r) - tsOf(b.r)) || (a.i - b.i))
    .forEach(({ r }) => {
      const w = r.window || 'unknown';
      out[r.studentId] = out[r.studentId] || {};
      out[r.studentId][w] = out[r.studentId][w] || {};
      const prev = out[r.studentId][w][r.testType];
      if (prev && r.administration === ADMIN_NOT_ADMINISTERED && prev.administration !== ADMIN_NOT_ADMINISTERED) return;
      out[r.studentId][w][r.testType] = r;
    });
  return out;
}

export function sessionProgress(session, stepTypes) {
  const steps = session?.steps || {};
  const done = stepTypes.filter((t) => {
    const s = steps[t];
    return !!s && (s.submitted || s.skipped || s.discontinued);
  }).length;
  return { done, total: stepTypes.length };
}

// ─── Teacher print report rows (StudentReport, v3.57.0 round 2) ─────────
/**
 * Rows for the PA section of the teacher print report: one per administered
 * (window, step) doc for THIS student, ordered window-then-step. Skipped and
 * never-administered steps produce no row — the report prints what happened,
 * not the full matrix (the full matrix lives on /pa/results).
 *
 * Same contract as selectBoyReportRows: returns [] when the flag is off, so
 * the section (and its heading) never renders in a flag-off environment even
 * if pa_* docs exist as inert data.
 */
export function selectPaReportRows(assessments, paStepDefs, flagEnabled) {
  if (!flagEnabled) return [];
  const windowOrder = PA_WINDOWS.map((w) => w.id);
  const windowLabels = Object.fromEntries(PA_WINDOWS.map((w) => [w.id, w.label]));
  const stepLabels = Object.fromEntries((paStepDefs || []).map((d) => [d.testType, d.label]));
  return (assessments || [])
    .filter(isScreenerDoc)
    .map((doc) => ({
      testType: doc.testType,
      window: doc.window,
      windowLabel: windowLabels[doc.window] || doc.window || '',
      label: stepLabels[doc.testType] || doc.testType,
      step: doc.screenerStep,
      score: doc.correctCount,
      total: totalPossibleOf(doc), // scale from the doc — NEVER assumed (Change 1)
      band: bandForScore(doc.correctCount, { administration: doc.administration }),
      administration: doc.administration || ADMIN_ADMINISTERED,
      notAdministered: doc.notAdministered ?? 0,
      timestamp: doc.timestamp,
    }))
    .sort((a, b) => {
      const w = windowOrder.indexOf(a.window) - windowOrder.indexOf(b.window);
      return w !== 0 ? w : (a.step ?? 0) - (b.step ?? 0);
    });
}
